import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Language } from '../Models/language';
import { Config } from './config';
import { Observable } from 'rxjs/Observable';
import { BehaviorSubject } from 'rxjs/BehaviorSubject';
import { Subject }    from 'rxjs/Subject';


/**
* The language service provides a list of supported languages and
* translations for those languages. The langChange$ event notifies
* listeners of language change.
*/
@Injectable() export class
LanguageService {

  private language = new BehaviorSubject<Language>(null);

  // event emitter for language change. This is not the same as the
  // behavior subject, in that it only emits on change!
  private langChangedSrc = new Subject<Language>();

  public langChange$ = this.langChangedSrc.asObservable();

  private lang_ctry_mapping: {[id: string]: string} = {
    'en': 'gb'
  };

  constructor(private http: HttpClient, private config: Config) {

    let fallbackLang = new Language("en", "gb", "English");

    let browserLocale = config.initialContentLang;

    if (browserLocale) {
      let country = (browserLocale in this.lang_ctry_mapping) ?
        this.lang_ctry_mapping[browserLocale] : browserLocale;
      fallbackLang = new Language(browserLocale, country);
    }

    let initialLang = this._getLanguage() || fallbackLang;

    // Initially set a hardcoded language (without translations)
    // this is needed because some templates expect a language to be set
    this.language.next(initialLang);

    // now load the default language with translations
    let langUrl = `${this.config.getURL('languages')}${initialLang.id}`;

    this.http.get<Language>(langUrl).subscribe(
        value => this.language.next(value),
    );
  }

  public list(): Observable<Language[]> {
    return this.http.get<Language[]>(this.config.getURL('languages'));
  }

  /**
   * User controlled setting of language.
   */
  public setLanguage(lang: Language): void {

    if (lang._links) {
      this.http.get<Language>(lang._links['self']).subscribe(
        value => {
          this.language.next(value);
          this.langChangedSrc.next(<Language>value);
        },
        error => this.language.next(lang)
      );
    }

    localStorage.setItem(this.config.lang_storage_key, JSON.stringify(lang));
  }

  /**
   * Return current language as observable.
   */
  public getLanguage(): Observable<Language> {

    return this.language.asObservable();
  }

  private _getLanguage(): Language {

    return JSON.parse(localStorage.getItem(this.config.lang_storage_key));
  }

  private replaceVars(trans: string, vars?: Map<string, any>) {

    if (vars) {
      return trans.replace(/\{([\w\s]*)\}/g,
                           function(key, m) { return vars.get(m) });
    } else {
      return trans
    }
  }

  /**
   * Translate the given key, if available. Otherwise return key.
   */
  public translate(key: string, emptyStringIfNone: boolean, vars?: Map<string, any>): Observable<string> {

    return this.getLanguage().map(
      data => {
        let translated = '';
        if (data.translations && key in data.translations) {
          translated = this.replaceVars(data.translations[key], vars);
        }
        if (!translated && !emptyStringIfNone) {
          translated = this.replaceVars(key, vars);
        }
        return translated;
      }
    );
  }
}



// WEBPACK FOOTER //
// ./src/app/Services/lang.ts